import { Node, Edge, getVisibleNodesInGroup } from "./formGraphTraversal";
import { initSearchableSelects } from "./searchableSelect";

export class FieldVisibilityManager {
  private container: Element;
  private groupId: string;
  private nodes: Node[];
  private edges: Edge[];
  private persistentValues: Record<string, FormDataEntryValue> = {};
  public isComplete: boolean = false;
  private onChangeCallback: () => void;

  constructor(
    container: Element,
    groupId: string,
    nodes: Node[],
    edges: Edge[],
    onChangeCallback: () => void
  ) {
    this.container = container;
    this.groupId = groupId;
    this.nodes = nodes;
    this.edges = edges;
    this.onChangeCallback = onChangeCallback;
  }

  init() {
    // Attach listeners
    this.attachListeners();
    // Initial values
    this.updateFormValues();
    this.updateVisibility();
  }

  private attachListeners() {
    this.container.addEventListener("change", (e) => this.handleInput(e));
    this.container.addEventListener("input", (e) => this.handleInput(e));
    this.container.addEventListener("focusout", (e) => this.handleBlur(e));
  }

  private handleBlur(e: Event) {
    const target = e.target as HTMLElement;
    if (target.matches("input, select, textarea")) {
      target.classList.add("form-flow-input-touched");
    }
  }

  private handleInput(_e: Event) {
    this.updateFormValues();
    this.updateVisibility();
    this.onChangeCallback();
  }

  private updateFormValues() {
    const form = this.container.closest("form");
    if (!form) {
      return;
    }
    const formData = new FormData(form);
    const currentValues = Object.fromEntries(formData.entries());

    // Merge current values into persistent values
    this.persistentValues = { ...this.persistentValues, ...currentValues };
  }

  public updateVisibility() {
    let stable = false;
    let iterations = 0;
    const maxIterations = 10;

    while (!stable && iterations < maxIterations) {
      iterations++;
      const visibleNodeIds = getVisibleNodesInGroup(
        this.groupId,
        this.nodes,
        this.edges,
        this.persistentValues
      );

      const groupNodes = this.nodes.filter((n) => n.parentId === this.groupId);

      // Sort by priority
      groupNodes.sort((a, b) => {
        const pA = a.data.priority || 0;
        const pB = b.data.priority || 0;
        return pA - pB;
      });

      let domChanged = false;

      for (const node of groupNodes) {
        const isVisible = visibleNodeIds.includes(node.id);
        const existingEl = this.container.querySelector(
          `[data-node-id="${node.id}"]`
        );

        if (isVisible) {
          if (!existingEl) {
            // Insert
            if (node.html) {
              const temp = document.createElement("div");
              temp.innerHTML = node.html;
              const newEl = temp.firstElementChild as HTMLElement;
              if (newEl) {
                // Restore value
                const savedVal = this.persistentValues[node.id];
                if (savedVal !== undefined && !(savedVal instanceof File)) {
                  const radioInputs = newEl.querySelectorAll(
                    'input[type="radio"]'
                  ) as NodeListOf<HTMLInputElement>;

                  if (radioInputs.length > 0) {
                    const radioValue = String(savedVal);
                    radioInputs.forEach((radio) => {
                      radio.checked = radio.value === radioValue;
                    });
                  } else {
                    const input = newEl.querySelector(
                      "input, select, textarea"
                    ) as
                      | HTMLInputElement
                      | HTMLSelectElement
                      | HTMLTextAreaElement;
                    if (input) {
                      input.value = savedVal;
                    }
                  }
                }

                // Find insertion point
                let insertAfter: Element | null =
                  this.container.querySelector("legend");
                const myIndex = groupNodes.indexOf(node);

                for (let i = myIndex - 1; i >= 0; i--) {
                  const prevNode = groupNodes[i];
                  const prevEl = this.container.querySelector(
                    `[data-node-id="${prevNode.id}"]`
                  );
                  if (prevEl) {
                    insertAfter = prevEl;
                    break;
                  }
                }

                if (insertAfter && insertAfter.nextElementSibling) {
                  insertAfter.insertAdjacentElement("afterend", newEl);
                } else {
                  this.container.appendChild(newEl);
                }
                initSearchableSelects(newEl);
                domChanged = true;
              }
            }
          }
        } else {
          if (existingEl) {
            existingEl.remove();
            domChanged = true;
          }
        }
      }

      if (domChanged) {
        const prevValuesJSON = JSON.stringify(this.persistentValues);
        this.updateFormValues();
        const newValuesJSON = JSON.stringify(this.persistentValues);

        if (prevValuesJSON !== newValuesJSON) {
          stable = false;
        } else {
          stable = true;
        }
      } else {
        stable = true;
      }

      if (stable) {
        this.checkCompletion(visibleNodeIds);
      }
    }
  }

  private checkCompletion(visibleNodeIds: string[]) {
    let complete = true;

    for (const nodeId of visibleNodeIds) {
      const node = this.nodes.find((n) => n.id === nodeId);
      if (node && node.data.required) {
        const val = this.persistentValues[nodeId];
        if (!val || (typeof val === "string" && val.trim() === "")) {
          complete = false;
          break;
        }
      }
    }
    this.isComplete = complete;
  }
}

export class FieldGroupsManager {
  private submitButton: HTMLButtonElement | null;
  private groups: Record<string, FieldVisibilityManager> = {};
  private nodes: Node[];
  private edges: Edge[];

  constructor(submitButton: HTMLElement | null, nodes: Node[], edges: Edge[]) {
    this.submitButton = submitButton as HTMLButtonElement | null;
    this.nodes = nodes;
    this.edges = edges;
    this.init();
  }

  init() {
    const groupElements = document.querySelectorAll(
      ".wp-form-flow-group:not(.wp-form-flow-group-completed)"
    );

    groupElements.forEach((el) => {
      const groupId = el.getAttribute("data-node-id");
      if (groupId) {
        this.groups[groupId] = new FieldVisibilityManager(
          el,
          groupId,
          this.nodes,
          this.edges,
          () => this.updateSubmitButtonState()
        );
        this.groups[groupId].init();
      }
    });

    this.updateSubmitButtonState();
  }

  updateSubmitButtonState() {
    if (!this.submitButton) {
      return;
    }

    // Always ensure it's enabled for interaction
    this.submitButton.removeAttribute("disabled");

    const allComplete = Object.values(this.groups).every((g) => g.isComplete);

    if (allComplete) {
      this.submitButton.classList.remove("wp-form-flow-submit-disabled");
    } else {
      this.submitButton.classList.add("wp-form-flow-submit-disabled");
    }
  }

  public isValid() {
    return Object.values(this.groups).every((g) => g.isComplete);
  }
}
